import {
    CapacityIcon,
    CartIcon,
    PassIcon,
    PiggyBankIcon,
    QuestionnaireIcon,
    RefreshIcon,
    TicketIcon,
    UserIcon,
} from "@xola/icons";
import { ColDef, ValueFormatterParams, ValueGetterFunc, ValueGetterParams } from "ag-grid-enterprise";
import { formatDate, now, PurchaseItemStatus } from "common";
import React, { ReactElement, ReactNode, useMemo } from "react";
import {
    GuestStatus,
    Permission,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseStatus,
    Report,
    ReportCollection,
    ReportObject,
    Seller,
    Transaction,
} from "types";
import { getQueryParams } from "../../../models/ReportFilterState";
import { ReportFilterOperator } from "./ReportFilterState";
import { useAuthorization } from "@/hooks/useAuthorization";

export interface ReportColumnDef extends ColDef {
    field: string;
    headerName: string;
    visible?: boolean; // Default true
    expand?: ReportColumnExpandDef; // Default false - For dynamic columns like quantity & tax fee
    type?: string | string[];
    parent?: {
        field: string;
        headerName: string;
    };
    valueGetter?: (params: ValueGetterParams) => string | number | boolean;
    valueFormatter?: (params: ValueFormatterParams) => string;
    filterValueType?: ReportColumnFilterValueType;
}

export interface ReportColumnExpandDef extends Partial<ReportColumnDef> {
    key: string;
    type: string | string[];
    minWidth?: number;
    maxWidth?: number;
    headerGetter?: (data: any, lineItemNames?: string[]) => string[];
    cellRendererParams?: any;
    cellRenderer?: (data: any) => ReactNode | null;
    valueGetter?: ValueGetterFunc;
    requiresLineItemsData?: boolean;
}

export enum ReportColumnFilterValueType {
    TIMESTAMP = "timestamp", // Date formatted as a Unix timestamp number
    DATE = "date", // Date formatted as an ISO string `YYYY-MM-DD`
    TIME = "time", // Time as "LT", // 8:49 AM
    STRING = "string",
    NUMBER = "number",
}

export interface SuggestedReport {
    description: string;
    icon: ReactElement;
    report: Report;
    beta?: boolean;
    hidden?: boolean;
    /** (Optional) Permission required for the report  */
    permission?: Permission;
}

const getStart = () => {
    return formatDate(now().subtract(1, "month").startOf("day"));
};

const getEnd = () => {
    return formatDate(now().endOf("day"));
};

export const useSuggestedReports = (): SuggestedReport[] => {
    const startDate = useMemo(() => getStart(), []);
    const endDate = useMemo(() => getEnd(), []);
    const { isGranted } = useAuthorization();
    // Ware-manager-only delegates get the Merchandise Inventory report exclusively.
    const hasOnlyWareManagerRole = !isGranted(Permission.NON_WARE_MANAGER_VIEW);

    const reports: SuggestedReport[] = [
        {
            description:
                "This report contains financial details for every charge or refund associated with your purchases",
            icon: <RefreshIcon />,
            report: {
                object: ReportObject.TRANSACTIONS,
                title: "Transactions",
                collection: ReportCollection.TRANSACTIONS,
                showSummary: true,
                excludedColumns: [],
                hiddenFields: ["context"],
                filters: getQueryParams({
                    context: { operator: ReportFilterOperator.EQ, value: "report" },
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                }),
            },
        },
        {
            description:
                "This report contains information on each individual purchases like customer name, email, total cost and purchase date",
            icon: <PiggyBankIcon />,
            report: {
                object: ReportObject.PURCHASES,
                title: "Purchases",
                collection: ReportCollection.PURCHASES,
                excludedColumns: [],
                hiddenFields: ["context", "status"],
                filters: getQueryParams({
                    status: {
                        operator: ReportFilterOperator.EQ,
                        value: PurchaseStatus.COMMITTED,
                    },
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                }),
            },
        },
        {
            description:
                "This report contains information on each individual reservation like quantity reserved, codes used, add-ons purchased, etc",
            icon: <PiggyBankIcon className="text-primary" />,
            report: {
                title: "Reservations",
                collection: ReportCollection.RESERVATIONS,
                object: ReportObject.RESERVATIONS,
                excludedColumns: [],
                hiddenFields: ["object", "context"],
                filters: getQueryParams({
                    status: {
                        operator: ReportFilterOperator.IN,
                        value: [
                            PurchaseItemStatus.CONFIRMED,
                            PurchaseItemStatus.DECLINED,
                            PurchaseItemStatus.ABANDONED,
                            PurchaseItemStatus.CANCELED,
                            PurchaseItemStatus.PENDING_ACCEPT,
                            PurchaseItemStatus.PENDING_MINIMUM,
                        ],
                    },
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                    object: {
                        operator: ReportFilterOperator.IN,
                        value: [PurchaseItemObject.EXPERIENCE, PurchaseItemObject.ORDER, PurchaseItemObject.EQUIPMENT],
                    },
                }),
            },
        },
        {
            description:
                "This report contains information about purchases that were abandoned, including a column indicating whether or not they were recovered and booked via Abandoned Purchase Recovery",
            icon: <CartIcon />,
            report: {
                object: ReportObject.ABANDONS,
                title: "Abandoned Reservations",
                collection: ReportCollection.DRAFTS,
                excludedColumns: [],
                hiddenFields: ["context", "include", "sort", "organizer.email"],
                filters: getQueryParams({
                    include: {
                        operator: ReportFilterOperator.EQ,
                        value: "audits",
                    },
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                    "organizer.email": {
                        // We don't want to include purchases without an email.
                        // NE Filter is not working, so using NIN with empty string
                        operator: ReportFilterOperator.NIN,
                        value: [""],
                    },
                    sort: {
                        operator: ReportFilterOperator.EQ,
                        value: "-createdAt",
                    },
                }),
            },
        },
        {
            description:
                "This report contains one row per product with aggregated information on all of the purchases associated with that experience",
            icon: <PiggyBankIcon />,
            report: {
                object: ReportObject.RESERVATIONS_BY_LISTING,
                title: "Reservations by Listing",
                collection: ReportCollection.RESERVATIONS,
                excludedColumns: [],
                hiddenFields: ["context", "object", "type", "groupBy", "items_arrivalDate", "guestStatus", "status"],
                filters: getQueryParams({
                    groupBy: {
                        operator: ReportFilterOperator.EQ,
                        value: "product",
                    },
                    type: {
                        operator: ReportFilterOperator.IN,
                        value: [PurchaseItemObject.EXPERIENCE, PurchaseItemObject.EQUIPMENT],
                    },
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                    status: {
                        operator: ReportFilterOperator.EQ,
                        value: PurchaseItemStatus.CONFIRMED,
                    },
                    guestStatus: {
                        operator: ReportFilterOperator.NIN,
                        value: GuestStatus.NO_SHOW,
                    },
                }),
            },
        },
        {
            description:
                "This report breaks down each purchase into it's most granular line items demographics (e.g. Adult, Child), taxes, fees, etc.",
            icon: <PiggyBankIcon />,
            report: {
                object: ReportObject.ITEMIZED_PURCHASES,
                title: "Itemized Purchases",
                collection: ReportCollection.PURCHASES,
                excludedColumns: [],
                filters: {},
            },
            beta: true,
            hidden: true,
        },
        {
            description: "View your customers' questionnaire responses",
            icon: <QuestionnaireIcon />,
            report: {
                object: ReportObject.QUESTIONNAIRE,
                title: "Questionnaire",
                collection: ReportCollection.RESERVATIONS,
                excludedColumns: [],
                hiddenFields: ["context", "status", "object"],
                filters: getQueryParams({
                    context: {
                        operator: ReportFilterOperator.EQ,
                        value: "questionnaire",
                    },
                    status: {
                        operator: ReportFilterOperator.NIN,
                        value: [PurchaseItemStatus.DRAFT, PurchaseItemStatus.HOLD],
                    },
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                    object: {
                        operator: ReportFilterOperator.IN,
                        value: [PurchaseItemObject.EXPERIENCE, PurchaseItemObject.EQUIPMENT],
                    },
                    expand: {
                        operator: ReportFilterOperator.EQ,
                        value: ["field.label"],
                    },
                }),
            },
        },
        {
            description:
                "This report contains information on capacity utilization for each event across all your experiences",
            icon: <CapacityIcon />,
            report: {
                object: ReportObject.CAPACITY,
                title: "Capacity",
                collection: ReportCollection.CAPACITY,
                excludedColumns: [],
                hiddenFields: ["context", "expand", "allowPastSlots"],
                filters: getQueryParams({
                    start: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                    expand: {
                        operator: ReportFilterOperator.EQ,
                        value: ["event.quantity", "event.resourceRestricted"],
                    },
                    allowPastSlots: {
                        operator: ReportFilterOperator.EQ,
                        value: true,
                    },
                }),
            },
        },
        {
            description: "This report contains information on membership passes",
            icon: <PassIcon />,
            permission: Permission.MEMBERSHIPS_VIEW,
            report: {
                title: "Memberships",
                collection: ReportCollection.RESERVATIONS,
                object: ReportObject.MEMBERSHIP,
                excludedColumns: [],
                hiddenFields: ["object", "context", "status"],
                filters: getQueryParams({
                    context: {
                        operator: ReportFilterOperator.EQ,
                        value: "memberships",
                    },
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                    object: {
                        operator: ReportFilterOperator.EQ,
                        value: PurchaseItemObject.MEMBERSHIP,
                    },
                    status: {
                        operator: ReportFilterOperator.IN,
                        value: [PurchaseItemStatus.CONFIRMED],
                    },
                }),
            },
        },
        {
            description: "This report displays the status of ticket redemption per guest across multiple attractions",
            icon: <TicketIcon />,
            report: {
                object: ReportObject.TICKET_REDEMPTION,
                title: "Ticket Redemption",
                collection: ReportCollection.SCANS,
                excludedColumns: [],
                hiddenFields: ["context"],
                filters: getQueryParams({
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                }),
            },
            hidden: true,
        },
        {
            description:
                "This report shows gross total for each product sold, including SKU, item price, quantity, and total revenue",
            icon: <PiggyBankIcon />,
            permission: Permission.MERCHANDISE_VIEW,
            report: {
                object: ReportObject.MERCHANDISE_GROSS_SALES,
                title: "Merchandise Gross Sales",
                collection: ReportCollection.RESERVATIONS,
                excludedColumns: [],
                hiddenFields: ["context", "status", "object", "lineItems.quantity"],
                filters: getQueryParams({
                    context: {
                        operator: ReportFilterOperator.EQ,
                        value: "ware_gross_sales",
                    },
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                    status: {
                        operator: ReportFilterOperator.NIN,
                        value: [PurchaseItemStatus.DRAFT, PurchaseItemStatus.HOLD],
                    },
                    object: {
                        operator: ReportFilterOperator.EQ,
                        value: "ware_purchase_item",
                    },
                    "lineItems.quantity": {
                        operator: ReportFilterOperator.GT,
                        value: 0,
                    },
                }),
            },
        },
        {
            description:
                "This report shows the current inventory state for all merchandise variants, including unavailable, committed, available, and on-hand quantities",
            icon: <CartIcon />,
            permission: Permission.MERCHANDISE_VIEW,
            report: {
                object: ReportObject.MERCHANDISE_INVENTORY,
                title: "Merchandise Inventory",
                collection: ReportCollection.CATALOG_ITEMS,
                excludedColumns: [],
                hiddenFields: ["context"],
                filters: getQueryParams({
                    context: {
                        operator: ReportFilterOperator.EQ,
                        value: "ware",
                    },
                    createdAt: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                }),
            },
        },
        {
            description:
                "This report contains a list of your customers, including contact details, lifetime value, and a history of their bookings",
            icon: <UserIcon />,
            permission: Permission.CUSTOMERS_EXPORT,
            report: {
                object: ReportObject.CUSTOMERS,
                title: "Customers",
                collection: ReportCollection.USERS,
                excludedColumns: [],
                hiddenFields: ["context", "type"],
                filters: getQueryParams({
                    updated: {
                        operator: ReportFilterOperator.RANGE,
                        value: [startDate, endDate],
                    },
                }),
            },
        },
    ];

    if (hasOnlyWareManagerRole) {
        return reports.filter(
            (report) =>
                report.report.object === ReportObject.MERCHANDISE_INVENTORY &&
                (!report.permission || isGranted(report.permission)),
        );
    }

    return reports.filter((report) => !report.permission || isGranted(report.permission));
};

export const getItemForTransaction = (transaction: Transaction, index = 0): PurchaseItem | undefined => {
    const itemId = transaction.items[index].orderItem?.id;
    return transaction.purchase?.items?.find((item: PurchaseItem) => item.id === itemId);
};

export interface CustomReportGridContext {
    seller: Seller;
    target?: "cell" | "tag";
}
